import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/**
 * React.lazy wrapper that survives a stale module graph.
 *
 * A "Failed to fetch dynamically imported module" error happens when the
 * browser asks for a code-split chunk that the dev server no longer serves
 * under that exact URL (server restart, hot reload, or a fresh deploy while a
 * tab was already open). Left alone, the rejected import throws during render
 * and blanks the whole screen. Here we retry the fetch, and if it still fails
 * we reload the page once so the browser picks up a fresh module graph.
 */

const RELOAD_TS_KEY = "ipe-chunk-reload-at";
const RELOAD_COOLDOWN_MS = 15000;
const RETRY_DELAY_MS = 350;
const MAX_ATTEMPTS = 3;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isChunkLoadError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|importing a module script failed|error loading dynamically imported module|loading chunk \d+ failed|failed to fetch/i.test(
    message
  );
};

/** Reloads at most once per cooldown window, so a real outage can't loop. */
const reloadOnce = () => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_TS_KEY) || 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
  } catch {
    // Private mode / storage disabled: don't reload blindly.
    return false;
  }
  window.location.reload();
  return true;
};

const Nothing = () => null;

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  attempts = MAX_ATTEMPTS
): LazyExoticComponent<T> {
  return lazy(async () => {
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        return await factory();
      } catch (error) {
        if (!isChunkLoadError(error)) throw error;
        if (attempt === attempts) {
          if (reloadOnce()) return { default: Nothing as unknown as T };
          throw error;
        }
        await sleep(RETRY_DELAY_MS * attempt);
      }
    }
    throw new Error("lazyWithRetry: unreachable");
  });
}
